import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { NodeTextField } from "../../NodeUI";
import "./CoverImageSetting.css";

/**
 * Cover-image URL input + live preview for the form/review-task/flow-step settings
 * panels. Single shared implementation so the three former copies (two of which had
 * drifted onto the legacy palette/sx styling) stay in sync.
 */
export default function CoverImageSetting({ value, onChange }) {
  const [previewError, setPreviewError] = useState(false);

  const handleChange = useCallback(
    (val) => {
      setPreviewError(false);
      onChange(val);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    setPreviewError(false);
    onChange("");
  }, [onChange]);

  const showPreview = value && !previewError;

  return (
    <div className="cover-setting">
      <div className="cover-setting__input-wrap">
        <NodeTextField
          label="Cover image URL"
          value={value || ""}
          onChange={handleChange}
          placeholder="https://example.com/image.jpg"
        />
        {value ? (
          <button
            type="button"
            className="cover-setting__clear"
            onClick={handleClear}
            aria-label="Clear cover image"
          >
            <CloseIcon />
          </button>
        ) : null}
      </div>
      {showPreview ? (
        <div className="cover-setting__preview">
          <img src={value} alt="Cover preview" onError={() => setPreviewError(true)} />
        </div>
      ) : (
        <div className="cover-setting__placeholder">
          <ImageOutlinedIcon />
          <span>{previewError ? "Could not load image" : "No cover image set"}</span>
        </div>
      )}
      <p className="cover-setting__hint">
        Displayed at the top of the public form page. Leave empty for an auto-generated gradient
        unique to this form.
      </p>
    </div>
  );
}

CoverImageSetting.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};
